import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useForm from '../../../hooks/useForm';
import Button from '../../shared/Button';
import CheckBox from '../../shared/CheckBox';
import ExternalLink from '../../shared/ExternalLink';
import Input from '../../shared/Input';
import Box from '../../shared/layouts/Box';
import Stack from '../../shared/layouts/Stack';
import PasswordCheckBox from '../../shared/PasswordCheckBox';
import Labeled from '../../shared/WithLabel';
import Container from './LoginContainer';
import Loader from '../../shared/Loader';
import WhiteLabelContext from '../../../context/WhiteLabelContext';
import {useTranslation} from "react-i18next";
import i18n from '../../../i18n';
import VerificationCodeForm from '../connection/VerificationCodeForm';
import Modal from '../../shared/Modal';
import LoginPage from "./LoginPage";

const CreateAccount = () => {
  const [state, handleState] = useForm({
    email: '',
    password: '',
    password_confirmation: '',
    terms_and_conditions: false,
    phone_number: null,
    'verbindings-url': '',
  });

  const [checks, setChecks] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSymbol: false,
    hasEightCharacters: false,
  });

  const { t } = useTranslation();
  const { companyName, platformName, termsAndConditionsUrl, registerWithSms } = useContext(WhiteLabelContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(true);
  const [email, setEmail] = useState(null);
  const [errors, setErrors] = useState({});
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [manualVerification, setManualVerification] = useState(false);
  const [showSmsCodeForm, setShowSmsCodeForm] = useState(false);
  const [hasConsumer, setHasConsumer] = useState(false);
  const [showManualVerificationForm, setShowManualVerificationForm] = useState(false);

  const { token = '' } = useParams();

  useEffect(() => {
    setIsPasswordValid(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*\W).{8,}$/g.test(state.password))
  }, [state.password])

  useEffect(() => {
    axios
      .get('/connection-code/check/' + token)
      .then(({ data }) => {
        if (data?.language) {
          i18n.changeLanguage(data.language);
        }

        if (data?.found === false || data.message) {
          setIsValidToken(false);
        } else {
          setEmail(data.email);
        }

        setHasConsumer(data.has_consumer);
        setManualVerification(data?.manual_verification);
        setIsLoading(false);
      })
      .catch(() => {
        setIsValidToken(false);
        setIsLoading(false);
      })
  }, [])

  const sendSms = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    axios
      .post('/aanmelden/verzend-sms', {
        ...state,
        terms_and_conditions: !termsAndConditionsUrl ? null : (state.terms_and_conditions ? 'on' : 'off'),
        'verbindings-url': token,
      })
      .then(() => {
        setShowSmsCodeForm(true);
      })
      .catch((err) => {
        const statusCode = err.response.status;

        if (statusCode === 429) {
          setShowSmsCodeForm(true);
          setErrors({
            "code": [
              t('Limiet berijkt! Probeer het na 30 seconden nogmaals')
            ]
          });
        } else if(statusCode === 422) {
          setShowSmsCodeForm(false);
          setErrors(err.response.data.errors || err.response.data.error);
        } else {
          setShowSmsCodeForm(true);
          setErrors({
            "code": [
              t('Er is iets fout gegaan. Probeer het opnieuw of neem contact op indien het fout blijft gaan.')
            ]
          });
        }
      })
      .finally(() => setIsLoading(false));
  }

  const postRegister = (payload) => {
    setIsLoading(true);

    axios
      .post('/aanmelden/met-code', payload)
      .then(() => {
        window.location.href = '/dashboard';
      })
      .catch((err) => {
        const errors = err.response.data.errors || err.response.data.error;
        setErrors(errors);
        setIsLoading(false);

        if (!errors) {
          return;
        }

        // If errors exist other than 'code', close verification modal.
        delete errors.code;
        if (Object.keys(errors).length > 0) {
          setShowManualVerificationForm(false);
        }
      });
  }

  const handleCodeSubmit = (code) => {
    postRegister(
      {
        ...state,
        terms_and_conditions: !termsAndConditionsUrl ? null : (state.terms_and_conditions ? 'on' : 'off'),
        'verbindings-url': token,
        code: code,
      }
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    if (manualVerification) {
      return setShowManualVerificationForm(true);
    }

    if (registerWithSms) {
      return sendSms(e);
    }

    postRegister(
      {
        ...state,
        terms_and_conditions: !termsAndConditionsUrl ? null : (state.terms_and_conditions ? 'on' : 'off'),
        'verbindings-url': token,
      }
    );
  };

  const handleOnChange = (e) => {
    const { value } = e.target;

    const checks = {
      hasLowercase: /[a-z]/.test(value),
      hasUppercase: /[A-Z]/.test(value),
      hasNumber: /\d/.test(value),
      hasSymbol: /\W/.test(value),
      hasEightCharacters: /^.{8,}$/.test(value),
    };

    setChecks(checks);
    handleState(e);
  };

  if (hasConsumer) {
    return <LoginPage email={email} />;
  }

  return (
    <Container handleSubmit={handleSubmit}>
      {isLoading && <Loader />}
      <Box fontScale="2xl" fontWeight="bold">
        {!isValidToken ? t('Uitnodiging niet gevonden') : t('Welkom bij {{companyName}}!', {companyName})}
      </Box>

      {!isValidToken && (
        <Box>{t('Neem contact met ons op en vraag om een nieuwe uitnodiging.')}</Box>
      )}

      <Modal
        isOpen={showSmsCodeForm}
        onRequestClose={() => setShowSmsCodeForm(false)}
        minWidth="600px"
      >
        <VerificationCodeForm
          infoText={t('Er is zojuist een sms verstuurd met daarin een verificatie code.')}
          onSubmit={handleCodeSubmit}
          onResend={sendSms}
          disableButtons={isLoading}
          error={errors?.code ? errors?.code[0] : null}
        />
      </Modal>

      <Modal
        isOpen={showManualVerificationForm}
        onRequestClose={() => setShowManualVerificationForm(false)}
        minWidth="600px"
      >
        <VerificationCodeForm
          infoText={t('Vul hier de code in die je hebt ontvangen van je contactpersoon.')}
          onSubmit={handleCodeSubmit}
          disableButtons={isLoading}
          error={errors?.code ? errors?.code[0] : null}
          hideResendButton
        />
      </Modal>

      {isValidToken && (
        <>
          <Box>{t('Maak een wachtwoord aan en krijg toegang tot {{platformName}}', {platformName})}</Box>
          <Stack space="m">
            <Labeled label={t("E-mailadres")}>
              <Input
                value={email}
                errors={errors?.email}
                disabled="disabled"
              />
            </Labeled>
            { !manualVerification && (
              <Labeled label={t("Telefoonnummer")}>
                <Input
                  name="phone_number"
                  value={state.phone_number}
                  onChange={handleState}
                  errors={errors?.phone_number}
                  placeholder={t("Vul je telefoonnummer in")}
                />
              </Labeled>
            )}
            <Labeled label={t("Wachtwoord")}>
              <Input
                name="password"
                type="password"
                value={state.password}
                onChange={handleOnChange}
                errors={errors?.password}
                placeholder={t("Vul je wachtwoord in")}
                autoComplete="new-password"
              />
            </Labeled>
          </Stack>
          <Box>
            <PasswordCheckBox value={checks.hasUppercase}>
              {'1 ' + t('Hoofdletter')}
            </PasswordCheckBox>
            <PasswordCheckBox value={checks.hasLowercase}>
              {'1 ' + t('Kleine letter')}
            </PasswordCheckBox>
            <PasswordCheckBox value={checks.hasNumber}>
              {'1 ' + t('Cijfer')}
            </PasswordCheckBox>
            <PasswordCheckBox value={checks.hasSymbol}>
              {'1 ' + t('Symbool')}
            </PasswordCheckBox>
            <PasswordCheckBox value={checks.hasEightCharacters}>
              {'8 ' + t('Karakters')}
            </PasswordCheckBox>
          </Box>
          <Labeled label={t("Herhaal wachtwoord")}>
            <Input
              name="password_confirmation"
              type="password"
              value={state.password_confirmation}
              onChange={handleState}
              errors={errors?.password_confirmation}
              placeholder={t("Vul nogmaals je wachtwoord in")}
              autoComplete="new-password"
            />
          </Labeled>
          {termsAndConditionsUrl && (
              <CheckBox
              value={state.terms_and_conditions}
              name="terms_and_conditions"
              title={
                <>
                  {t('Ik ga akkoord met de') + ' '}
                  <ExternalLink
                    display={'contents'}
                    href={termsAndConditionsUrl}
                  >
                    {t('algemene voorwaarden')}
                  </ExternalLink>
                </>
              }
              onChange={handleState}
              errors={errors?.terms_and_conditions}
              css={{
                fontSize: '14px',
                span: {
                  width: '24px',
                  height: '24px',
                },
              }}
            />
          )}
          <Button disabled={!isPasswordValid} variant="primary" width="100%" type="submit">
            {t('Account aanmaken')}
          </Button>
        </>
      )}
    </Container>
  );
};
export default CreateAccount;
